import React, { useEffect, useState } from 'react';
import {
    Box, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Typography, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Chip
} from '@mui/material';
import { LeavePolicyService } from '../../services/leave-policy.service';
import { InstitutionService } from '../../services/institution.service';
import { LeaveTypeService } from '../../services/leave-type.service';
import { LeavePolicy, LeaveTypeConfig } from '../../interfaces/leave.interface';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';

const MONTHS = [
    { value: 1, label: 'January' }, { value: 2, label: 'February' }, { value: 3, label: 'March' },
    { value: 4, label: 'April' }, { value: 5, label: 'May' }, { value: 6, label: 'June' },
    { value: 7, label: 'July' }, { value: 8, label: 'August' }, { value: 9, label: 'September' },
    { value: 10, label: 'October' }, { value: 11, label: 'November' }, { value: 12, label: 'December' }
];

const LeavePolicyManagement: React.FC = () => {
    const [policies, setPolicies] = useState<LeavePolicy[]>([]);
    const [leaveTypes, setLeaveTypes] = useState<LeaveTypeConfig[]>([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [leaveType, setLeaveType] = useState<string>('');
    const [numberOfLeaves, setNumberOfLeaves] = useState<number>(0);
    const [fiscalYearStartMonth, setFiscalYearStartMonth] = useState<number>(1);
    const toast = useToast();

    useEffect(() => {
        fetchPolicies();
        fetchInstitutionDetails();
        fetchLeaveTypes();
    }, []);

    const fetchLeaveTypes = async () => {
        try {
            const data = await LeaveTypeService.getByInstitution(true);
            setLeaveTypes(data);
            if (data.length > 0 && !leaveType) {
                setLeaveType(data[0].name);
            }
        } catch (err) {
            // ignore
        }
    };

    const fetchPolicies = async () => {
        try {
            const data = await LeavePolicyService.getPoliciesByInstitution();
            setPolicies(data);
        } catch (err) {
            toast.showApiError(err, 'Failed to fetch leave policies');
        } finally {
            setLoading(false);
        }
    };

    const fetchInstitutionDetails = async () => {
        try {
            const institutions = await InstitutionService.getLoggedInUserInstitutions();
            const selectedId = AuthService.getSelectedInstitution();
            const institution = institutions.content?.find((i: any) => i.id === selectedId);
            if (institution?.fiscalYearStartMonth) {
                setFiscalYearStartMonth(institution.fiscalYearStartMonth);
            }
        } catch (err) {
            // Ignore - default to January
        }
    };

    const handleSaveFiscalMonth = async () => {
        try {
            const institutionId = AuthService.getSelectedInstitution()!!;
            const current = await InstitutionService.getInstitutionById(institutionId);
            await InstitutionService.updateInstitution(institutionId, { ...current, fiscalYearStartMonth });
            toast.showSuccess('Fiscal year start month updated');
        } catch (err) {
            toast.showApiError(err, 'Failed to update fiscal year start month');
        }
    };

    const handleCreateOrUpdate = async () => {
        try {
            await LeavePolicyService.createOrUpdate({
                institutionId: AuthService.getSelectedInstitution()!!,
                leaveType,
                numberOfLeaves
            });
            setDialogOpen(false);
            setNumberOfLeaves(0);
            await fetchPolicies();
        } catch (err) {
            toast.showApiError(err, 'Failed to save leave policy');
        }
    };

    const handleToggleActive = async (id: string, currentlyActive: boolean) => {
        const nextActive = !currentlyActive;
        const verb = nextActive ? 'reactivate' : 'deactivate';
        if (!window.confirm(`Are you sure you want to ${verb} this policy?`)) return;
        try {
            await LeavePolicyService.setActiveStatus(id, nextActive);
            setPolicies(prev => prev.map(p =>
                p.id === id ? { ...p, isActive: nextActive } : p
            ));
            toast.showSuccess(nextActive ? 'Policy reactivated' : 'Policy deactivated');
        } catch (err) {
            toast.showApiError(err, `Failed to ${verb} policy`);
        }
    };

    const handleEditClick = (policy: LeavePolicy) => {
        setLeaveType(policy.leaveType);
        setNumberOfLeaves(policy.numberOfLeaves);
        setDialogOpen(true);
    };

    if (loading) return <Typography>Loading...</Typography>;

    return (
        <Box data-help-page="leave.policy.page" sx={{ padding: 3 }}>
            <Typography variant="h5" sx={{ marginBottom: 3 }}>Leave Policy Management</Typography>

            <Box data-help-section="leave.policy.filters" sx={{ display: 'flex', gap: 2, alignItems: 'center', marginBottom: 3 }}>
                <TextField
                    data-help="leave.policy.fiscalMonth"
                    select
                    label="Fiscal Year Start Month"
                    value={fiscalYearStartMonth}
                    onChange={(e) => setFiscalYearStartMonth(Number(e.target.value))}
                    sx={{ minWidth: 200 }}
                    size="small"
                >
                    {MONTHS.map((m) => (
                        <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                    ))}
                </TextField>
                <Button data-help="leave.policy.saveFiscalMonth" variant="outlined" onClick={handleSaveFiscalMonth}>
                    Save Fiscal Month
                </Button>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 2 }}>
                <Button data-help="leave.policy.add" variant="contained" onClick={() => { setLeaveType(leaveTypes[0]?.name ?? ''); setNumberOfLeaves(0); setDialogOpen(true); }}>
                    Add Policy
                </Button>
            </Box>

            <TableContainer data-help-section="leave.policy.table" component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell data-help="leave.policy.col.leaveType">Leave Type</TableCell>
                            <TableCell data-help="leave.policy.col.days">Number of Leaves</TableCell>
                            <TableCell data-help="leave.policy.col.status">Status</TableCell>
                            <TableCell data-help="leave.policy.col.actions">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {policies.map((policy) => {
                            const active = policy.isActive !== false;
                            return (
                            <TableRow key={policy.id} sx={{ opacity: active ? 1 : 0.55 }}>
                                <TableCell>{policy.leaveType}</TableCell>
                                <TableCell>{policy.numberOfLeaves}</TableCell>
                                <TableCell>
                                    <Chip
                                        label={active ? 'Active' : 'Inactive'}
                                        color={active ? 'success' : 'default'}
                                        size="small"
                                        variant={active ? 'filled' : 'outlined'}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Button data-help="leave.policy.action.edit" size="small" onClick={() => handleEditClick(policy)}>Edit</Button>
                                    <Button
                                        size="small"
                                        color={active ? 'warning' : 'success'}
                                        data-help="leave.policy.action.toggleActive"
                                        onClick={() => handleToggleActive(policy.id, active)}
                                    >
                                        {active ? 'Deactivate' : 'Reactivate'}
                                    </Button>
                                </TableCell>
                            </TableRow>
                            );
                        })}
                        {policies.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={4} align="center">No policies configured</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} PaperProps={{ 'data-help-page': 'leave.policyForm.page' } as any} maxWidth="sm" fullWidth>
                <DialogTitle>Leave Policy</DialogTitle>
                <DialogContent data-help-section="leave.policyForm.section.details">
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            select
                            data-help="leave.policyForm.field.leaveType"
                            label="Leave Type"
                            value={leaveType}
                            onChange={(e) => setLeaveType(e.target.value)}
                            fullWidth
                        >
                            {leaveTypes.map((lt) => (
                                <MenuItem key={lt.id} value={lt.name}>{lt.name}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            data-help="leave.policyForm.field.days"
                            label="Number of Leaves"
                            type="number"
                            value={numberOfLeaves}
                            onChange={(e) => setNumberOfLeaves(Number(e.target.value))}
                            fullWidth
                            inputProps={{ min: 0 }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button data-help="leave.policyForm.cancel" onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button data-help="leave.policyForm.save" variant="contained" onClick={handleCreateOrUpdate}>Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LeavePolicyManagement;
